/**
 * Coach eligibility for the `/book` coach chooser.
 *
 * A coach may teach a service iff their `serviceIds` — the services they hold an
 * active `InstructorService` offering for, as computed by `/api/booking/filters`
 * under the full public bookability gate — contains the selected service. When
 * no service is selected yet, the chooser shows the whole roster.
 *
 * Pure and generic over the coach shape so the booking flow and its tests share
 * one rule. Previously this predicate was inlined twice in `booking-flow.tsx`
 * (the empty-state check and the render map), which is how the storefront's
 * wrong-param bug went unnoticed: with no service ever selected, the filter
 * silently fell through to "show everyone".
 *
 * Note: this filters the *displayed* list. It is not the integrity gate — the
 * availability API (`src/lib/slot-generation.ts`) and the booking POST
 * (`src/app/api/bookings/route.ts`) independently reject an instructor who lacks
 * an `InstructorService` for the service, so an ineligible pairing can never be
 * booked even if the list were wrong.
 */
export function coachesForService<C extends { serviceIds: string[] }>(
  coaches: readonly C[],
  selectedServiceId: string | null | undefined,
): C[] {
  if (!selectedServiceId) return [...coaches];
  return coaches.filter((coach) => coach.serviceIds.includes(selectedServiceId));
}
